import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import { SignInForm } from '../../components/Auth/SignInForm/SignInForm.component'
import { ResetPasswordForm } from '../../components/Auth/ResetPasswordForm/ResetPasswordForm.component'
import { containsEmail } from '../../utils/functions'
import { theme } from '../../styles/theme.style'
import ChangeLanguageRow from '../../components/Settings/UserSettings/ChangeLanguageRow'
import { BrandedButton } from '../../components/Shared/BrandedButton/BrandedButton.component'
import styles from './Auth.page.module.scss'

const Auth = () => {
  const { t } = useTranslation()

  // Parse auto-login params from URL
  const urlParams = new URLSearchParams(window.location.search)
  const urlUsername = urlParams.get('username') || ''
  const urlPassword = urlParams.get('password') || ''
  const hasAutoLogin = urlUsername.length > 0 && urlPassword.length > 0

  const [showSignIn, setShowSignIn] = useState(hasAutoLogin)
  const [showPasswordReset, setShowPasswordReset] = useState(false)
  const [email, setEmail] = useState(urlUsername || '')
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState('')
  const [prefill, setPrefill] = useState(!hasAutoLogin)
  const [idps, setIdps] = useState({})

  const handleLogin = (e) => {
    setEmail(e.target.value)
  }

  useEffect(() => {
    if (email.length > 0) {
      if (!containsEmail(email)) {
        setError(true)
        setErrorText(t('Please enter a valid email address'))
      } else {
        setError(false)
        setErrorText('')
      }
    }
  }, [email])

  const getSAMLURL = (idp) => {
    return `https://${
      import.meta.env.VITE_oath_domain
    }/oauth2/authorize?identity_provider=${idp}&client_id=${
      import.meta.env.VITE_aws_user_pools_web_client_id
    }&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(
      import.meta.env.VITE_FRONTEND
    )}`
  }

  const redirectToSAML = (idp) => {
    window.location.assign(getSAMLURL(idp))
  }

  const submitPrefill = () => {
    const domain = email.split('@')[1]
    if (Object.keys(idps).includes(domain.toLowerCase())) {
      redirectToSAML(idps[domain.toLowerCase()])
    } else {
      setPrefill(false)
      setShowSignIn(true)
    }
  }

  const submitShowResetPassword = () => {
    setShowSignIn(false)
    setShowPasswordReset(true)
  }

  const submitShowSignIn = () => {
    setShowPasswordReset(false)
    setShowSignIn(true)
  }

  // Strip auto-login params from URL
  useEffect(() => {
    if (hasAutoLogin) {
      const url = new URL(window.location.href)
      url.searchParams.delete('username')
      url.searchParams.delete('password')
      window.history.replaceState({}, '', url.pathname + url.search + url.hash)
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    const abortController = new AbortController()

    const getAllIdps = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_BACKEND}/users/idps`,
          { signal: abortController.signal }
        )
        const idps = await response.json()
        if (isMounted && idps.length > 0) {
          const idpsObject = {}
          for (const idp of idps) {
            idpsObject[idp.domain] = idp.name
          }
          setIdps(idpsObject)
        }
      } catch (error) {
        if (error.name !== 'AbortError' && isMounted) {
          // Handle error if needed
          console.error('Error fetching IDPs:', error)
        }
      }
    }

    getAllIdps()

    return () => {
      isMounted = false
      abortController.abort()
    }
  }, [])

  return (
    <div className={styles.signIn}>
      <div className={styles.language}>
        <ChangeLanguageRow showLabel={false} variant="dark" />
      </div>
      <div className={styles.logoContainer}>
        <img className={styles.logoWhite} src="/assets/pngs/TeleVUWhite.png" alt="Logo" />
      </div>
      <div className={styles.centerContent}>
        <div className={styles.signInBox}>
          {showSignIn && <SignInForm prefilledEmail={email} prefilledPassword={urlPassword} autoLogin={hasAutoLogin} />}
          {showPasswordReset && <ResetPasswordForm prefilledEmail={email} />}
          {prefill && (
            <>
              <TextField
                id="outlined-basic"
                label={t('Email')}
                error={error}
                value={email}
                onChange={handleLogin}
                helperText={errorText}
                fullWidth
                sx={{ 
                  marginBottom: 2,
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(52, 148, 182, 0.3)',
                    padding: '8px 14px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.25)',
                    },
                    '& input': {
                      color: 'white !important',
                      fontSize: '1rem',
                      padding: '0',
                      '&:-webkit-autofill': {
                        WebkitTextFillColor: 'white !important',
                        WebkitBoxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                        boxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                        transition: 'background-color 5000s ease-in-out 0s',
                      },
                      '&:-webkit-autofill:hover': {
                        WebkitTextFillColor: 'white !important',
                        WebkitBoxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                        boxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                      },
                      '&:-webkit-autofill:focus': {
                        WebkitTextFillColor: 'white !important',
                        WebkitBoxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                        boxShadow: '0 0 0 1000px rgba(52, 148, 182, 0.3) inset !important',
                      },
                    },
                    '& input::placeholder': {
                      color: 'rgba(255, 255, 255, 0.7)',
                    },
                    color: 'white',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'white',
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: 'white',
                  },
                  '& .MuiFormHelperText-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                  },
                }}
                onKeyPress={(event) => {
                  if (event.key === 'Enter') {
                    submitPrefill()
                  }
                }}
              />
              <BrandedButton
                onClick={submitPrefill}
                disabled={error || email.length === 0}
                buttonLabel={'Login'}
              />
            </>
          )}
          <div className={styles.signInFooter}>
            {showSignIn && (
              <a
                className={styles.signInFooter__btn}
                onClick={submitShowResetPassword}
              >
                {t('Forgot Password')}
              </a>
            )}
            {showPasswordReset && (
              <a
                className={styles.signInFooter__btn}
                onClick={submitShowSignIn}
              >
                {t('Back To Sign In')}
              </a>
            )}
          </div>
          <div className={styles.signInFooter}>
            <a
              className={styles.signInFooter__btn}
              href="https://televu.ca/terms-conditions/"
              target="_blank"
              rel="noreferrer"
            >
              {t('Terms of Service')}
            </a>
            <a
              className={styles.signInFooter__btn}
              href={theme.PRIVACY_POLICY}
              target="_blank"
              rel="noreferrer"
            >
              {t('Privacy Policy')}
            </a>
          </div>
        </div>
      </div>
      <div className={styles.reserved}>
        <div>
          © {new Date().getFullYear()} {t('All Rights Reserved.')}
        </div>
        <div>{theme.COMPANY_NAME}</div>
      </div>
    </div>
  )
}

export default Auth
